import { useEffect, useMemo, useState } from "react";
import useTitle from "hooks/useTitle";
import usePermisosContable from "./usePermisosContable";
import {
  SISTEMA_CONTABLE_DASHBOARD_RESOURCE,
  SISTEMA_CONTABLE_MENU_SECTIONS,
} from "../constants";

const getResource = (resource) => resource || SISTEMA_CONTABLE_DASHBOARD_RESOURCE;

const DEFAULT_ICON = "📄";

/** IDs definidos en la constante estática */
const KNOWN_IDS = new Set(
  SISTEMA_CONTABLE_MENU_SECTIONS.flatMap((s) => s.items.map((i) => i.id)),
);

/**
 * Filtra SISTEMA_CONTABLE_MENU_SECTIONS para mostrar solo los ítems
 * cuyo id esté en los permisos del API.
 * Si el API devuelve un id que no existe en la constante, se agrega
 * al final con un icono estándar de documentos.
 */
const buildAllowedSections = (permissions) => {
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return [];
  }

  const allowedIds = new Set(permissions.map((p) => p.id));

  // Secciones conocidas filtradas por permisos
  const knownSections = SISTEMA_CONTABLE_MENU_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => allowedIds.has(item.id)),
  })).filter((section) => section.items.length > 0);

  // IDs nuevos: vienen del API, no están en la constante y tienen menuPadre != null
  const unknownItems = permissions
    .filter((p) => !KNOWN_IDS.has(p.id) && p.menuPadre !== null)
    .map((p) => ({
      id: p.id,
      icon: DEFAULT_ICON,
      nameMenu: p.nameMenu || p.id,
      titlePage: p.nameMenu || p.id,
      titleTab: p.nameMenu || p.id,
    }));

  if (unknownItems.length === 0) return knownSections;

  return [...knownSections, { title: "Otros", items: unknownItems }];
};

const useSistemaContable = () => {
  // El recurso seleccionado y los permisos del modulo viven en estado local
  // para no pisar state.resources del menu de facturacion.
  const [actualContent, setActualContent] = useState(SISTEMA_CONTABLE_DASHBOARD_RESOURCE);
  const { permissions, updatePermissons } = usePermisosContable();

  const currentResource = useMemo(
    () => getResource(actualContent),
    [actualContent],
  );

  const menuSections = useMemo(
    () => buildAllowedSections(permissions),
    [permissions],
  );

  useTitle({ title: currentResource.titleTab });

  useEffect(() => {
    updatePermissons();
  }, [updatePermissons]);

  const selectResource = (resource) => {
    setActualContent(resource);
  };

  return {
    currentResource,
    menuSections,
    selectResource,
    title: currentResource.titlePage,
  };
};

export default useSistemaContable;
